import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    MdAttachMoney,
    MdAutorenew,
    MdCalendarMonth,
    MdCheckCircle,
    MdCreditCard,
    MdEventBusy,
    MdHistory,
    MdOutlinePayments,
    MdPauseCircle,
    MdPerson,
    MdCardMembership,
    MdEmail,
    MdPhone,
    MdLocationOn,
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { getSubscriberById, getAllSubscribers } from '../../Services/SubscriptionService';
import ExpandableTransactionPanel from '../../Components/Common/ExpandableTransactionPanel';
import { APPRoutes } from '../../Utils/Route';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { formatUtcToLocalDateTime } from '../../Utils/DateTime';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import BackButton from '../../Components/Common/BackButton';

const SUBSCRIPTION_STATUS_STYLE = {
    Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text-strong)', icon: MdCheckCircle },
    Inactive: { backgroundColor: 'var(--hc-status-neutral-bg)', color: 'var(--hc-table-footer-text)', icon: MdPauseCircle },
    Unknown: { backgroundColor: 'var(--hc-status-unknown-bg)', color: 'var(--hc-status-unknown-text)', icon: MdPauseCircle },
};

const PAYMENT_STATUS_STYLE = {
    Paid: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text-strong)' },
    Unpaid: { backgroundColor: 'var(--hc-status-pending-bg)', color: 'var(--hc-status-pending-text)' },
    Cancelled: { backgroundColor: 'var(--hc-status-danger-bg)', color: 'var(--hc-status-danger-text-strong)' },
    Expired: { backgroundColor: 'var(--hc-status-neutral-bg)', color: 'var(--hc-table-footer-text)' },
    Unknown: { backgroundColor: 'var(--hc-status-unknown-bg)', color: 'var(--hc-status-unknown-text)' },
};

const toCurrency = (value) => {
    const amount = Number(value);

    if (!Number.isFinite(amount)) {
        return '-';
    }

    return `$${amount.toLocaleString()}`;
};

const toCapitalized = (value) => {
    if (!value || value === '-') {
        return '-';
    }

    const textValue = String(value).toLowerCase();
    return textValue.charAt(0).toUpperCase() + textValue.slice(1);
};

const normalizeSubscriber = (subscriber) => {
    if (!subscriber) {
        return null;
    }

    return {
        ...subscriber,
        subscription_status_name: subscriber?.subscription_status_name || 'Unknown',
        payment_status_name: subscriber?.payment_status_name || 'Unknown',
    };
};

const ViewSkeleton = () => (
    <div className="container-fluid py-4 hc-page-bg min-vh-100">
        <div className="d-flex gap-2 mb-3">
            <div className="skeleton position-relative overflow-hidden rounded-pill" style={{ height: 40, width: 110 }} />
        </div>

        <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-teal">
            <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
            <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
            <div className="position-relative">
                <div className="skeleton-hero mb-3 rounded-3" style={{ height: 28, width: '55%' }} />
                <div className="skeleton-hero mb-2 rounded-2" style={{ height: 18, width: '38%' }} />
                <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 18, width: '28%' }} />
            </div>
        </div>

        <div className="row g-4">
            {[1, 2, 3, 4].map((item) => (
                <div className="col-lg-3 col-md-6" key={item}>
                    <div className="skeleton position-relative overflow-hidden rounded-4" style={{ height: 120 }} />
                </div>
            ))}
            <div className="col-lg-7">
                <div className="skeleton position-relative overflow-hidden rounded-4" style={{ height: 250 }} />
            </div>
            <div className="col-lg-5">
                <div className="skeleton position-relative overflow-hidden rounded-4" style={{ height: 250 }} />
            </div>
        </div>
    </div>
);

const StatCard = ({ label, value, icon: Icon, iconBg, accentClass, valueClass }) => (
    <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
        <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${accentClass}`} />
        <div
            className="d-inline-flex align-items-center justify-content-center rounded-3"
            style={{ width: 44, height: 44, backgroundColor: iconBg }}
        >
            <Icon size={20} />
        </div>
        <div className="d-flex flex-column fw-bold lh-sm">
            <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                {label}
            </span>
            <span className={`fw-bold fs-5 ${valueClass}`}>
                {value}
            </span>
        </div>
    </div>
);

const DetailRow = ({ icon: Icon, label, value }) => (
    <div className="d-flex align-items-start gap-3 py-3 border-bottom border-light-subtle">
        <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: 36, height: 36, background: 'linear-gradient(135deg, rgba(var(--hc-accent-blue-rgb), 0.08) 0%, rgba(var(--hc-accent-blue-rgb), 0.12) 100%)', color: 'var(--hc-accent-blue)' }}
        >
            <Icon size={16} />
        </span>
        <div className="flex-grow-1 min-w-0">
            <div className="small text-muted mb-1">{label}</div>
            <div className="fw-medium text-dark text-break" style={{ fontSize: '0.9rem' }}>
                {value || '-'}
            </div>
        </div>
    </div>
);

export default function SubscribersView() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { token, permissions, decodedToken } = useAuth();

    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken],
    );

    const canViewSubscriber = permissionHas(userPermissionCodes, PermissionCodes.SUBSCRIBERS_VIEW, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, PermissionCodes.SUBSCRIBERS_LIST, decodedToken);

    const [subscriber, setSubscriber] = React.useState(
        normalizeSubscriber(location.state?.subscriber || null),
    );
    const [loading, setLoading] = React.useState(!location.state?.subscriber);

    const targetSubscriberId = React.useMemo(() => {
        if (!id || id === 'first' || id === ':id') return null;
        return id;
    }, [id]);

    React.useEffect(() => {
        if (!token) {
            return;
        }

        if (location.state?.subscriber) {
            return;
        }

        (async () => {
            try {
                setLoading(true);
                let resolvedId = targetSubscriberId;
                if (!resolvedId) {
                    const listRes = await getAllSubscribers(token);
                    const rawList = listRes?.data?.subscribers || listRes?.data?.Subscribers || listRes?.data || listRes;
                    const list = Array.isArray(rawList) ? rawList : [];
                    if (list.length > 0) {
                        resolvedId = list[0]?.id || list[0]?.Id;
                    }
                }

                if (!resolvedId) {
                    setSubscriber(null);
                    return;
                }

                const response = await getSubscriberById(resolvedId, token);

                if (response?.is_success) {
                    setSubscriber(normalizeSubscriber(response?.data || null));
                } else if(response?.is_success === false && response?.status_code === 500) {
                    toast.error('Internal Server Error. Please Contact Support Team.');
                    setSubscriber(null);
                } else {
                    setSubscriber(null);
                    toast.error(response?.message || 'Failed to load subscriber details');
                }
            } catch (error) {
                console.error(error);
                setSubscriber(null);
                toast.error('An error occurred while loading subscriber details');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, targetSubscriberId, token, location.state?.subscriber]);

    if (!canViewSubscriber) {
        return (
            <div className="container-fluid py-4">
                <div className="p-4 bg-white rounded-4 shadow-sm">
                    <h5 className="mb-2">Permission denied</h5>
                    <p className="text-muted">You do not have permission to view this subscriber.</p>
                    <button type="button" className="btn btn-outline-primary" onClick={() => navigate(APPRoutes.SUBSCRIPTION_SUBSCRIBERS)}>
                        Back to Subscribers
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return <ViewSkeleton />;
    }

    if (!subscriber) {
        return (
            <ViewEmptyState
                title="No Subscribers Available"
                description="There are no subscriber records available to display."
                icon={MdCardMembership}
                canViewList={canViewList}
                backRoute={APPRoutes.SUBSCRIPTION_SUBSCRIBERS}
                backLabel="Back to Subscribers"
            />
        );
    }

    const statusName = subscriber?.subscription_status_name || 'Unknown';
    const paymentName = subscriber?.payment_status_name || 'Unknown';
    const statusMeta = SUBSCRIPTION_STATUS_STYLE[statusName] || SUBSCRIPTION_STATUS_STYLE.Unknown;
    const StatusIcon = statusMeta.icon;
    const paymentMeta = PAYMENT_STATUS_STYLE[paymentName] || PAYMENT_STATUS_STYLE.Unknown;

    const isRecurring = Boolean(subscriber?.subscription_price?.is_recurring);
    const billingMethod = subscriber?.subscription_price?.billing_method || '-';
    const planName = subscriber?.subscription_plan?.name || '-';
    const clientName = subscriber?.client?.name || '-';

    const createdOn = formatUtcToLocalDateTime(subscriber?.created_on);
    const modifiedOn = formatUtcToLocalDateTime(subscriber?.modified_on);
    const startDate = formatUtcToLocalDateTime(subscriber?.start_date);
    const endDate = formatUtcToLocalDateTime(subscriber?.end_date);

    return (
        <div className="container-fluid py-4">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList && (
                    <BackButton onClick={() => navigate(APPRoutes.SUBSCRIPTION_SUBSCRIBERS)} />
                )}

                <span className="small text-muted">
                    Subscriber ID <strong className="hc-text-primary">#{subscriber?.id || '-'}</strong>
                </span>
            </div>

            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-teal">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-8">
                        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                            <span
                                className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold"
                                style={{ fontSize: '0.82rem', ...statusMeta }}
                            >
                                {statusName}
                            </span>
                            <span
                                className="px-3 py-2 rounded-pill fw-semibold"
                                style={{
                                    fontSize: '0.82rem',
                                    backgroundColor: paymentMeta.backgroundColor,
                                    color: paymentMeta.color,
                                }}
                            >
                                {paymentName}
                            </span>
                            <span
                                className="px-3 py-2 rounded-pill"
                                style={{ fontSize: '0.82rem', backgroundColor: 'rgba(var(--hc-white-rgb), 0.12)', color: 'var(--hc-white)' }}
                            >
                                {isRecurring ? 'Recurring' : 'One-time'}
                            </span>
                        </div>

                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
                            {clientName}
                        </h2>
                        <p className="mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)', fontSize: '0.95rem' }}>
                            Enrolled in <span className="fw-semibold text-white">{planName}</span>
                        </p>
                        <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)', fontSize: '0.88rem' }}>
                            Pricing and billing status for this subscriber account.
                        </p>
                    </div>

                    <div className="col-lg-4">
                        <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
                            <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                Price
                            </p>
                            <p className="fw-bold text-white mb-3" style={{ fontSize: '1.55rem', lineHeight: 1.1 }}>
                                {toCurrency(subscriber?.subscription_price?.price)}
                            </p>
                            <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                Billing Method
                            </p>
                            <p className="fw-semibold text-white mb-0">
                                {toCapitalized(billingMethod)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* <div className="row g-3 mb-4">
				<div className="col-12 col-sm-6 col-xl-3">
					<StatCard
						label="Subscription Price"
						value={toCurrency(subscriber?.subscription_price?.price)}
						icon={MdAttachMoney}
						iconBg="var(--hc-status-success-bg)"
						accentClass="border-success"
						valueClass="text-success"
					/>
				</div>
				<div className="col-12 col-sm-6 col-xl-3">
					<StatCard
						label="Billing Cycle"
						value={toCapitalized(billingMethod)}
						icon={MdOutlinePayments}
						iconBg="var(--hc-icon-blue-bg)"
						accentClass="border-primary"
						valueClass="text-primary"
					/>
				</div>
				<div className="col-12 col-sm-6 col-xl-3">
					<StatCard
						label="Payment Status"
						value={paymentName}
						icon={MdCreditCard}
						iconBg="var(--hc-status-pending-bg)"
						accentClass="border-warning"
						valueClass="text-warning"
					/>
				</div>
				<div className="col-12 col-sm-6 col-xl-3">
					<StatCard
						label="Recurrence"
						value={isRecurring ? 'Enabled' : 'Disabled'}
						icon={MdAutorenew}
						iconBg="var(--hc-status-info-bg)"
						accentClass="border-info"
						valueClass="text-info"
					/>
				</div>
			</div> */}

            <div className="row g-4">
                <div className="col-lg-7">
                    <div className="hc-surface hc-card-primary mb-3">
                        <div className="px-4 pt-4 pb-2 d-flex align-items-center gap-2">
                            <span
                                className="d-inline-flex align-items-center justify-content-center rounded-3"
                                style={{ width: 34, height: 34, backgroundColor: 'rgba(var(--hc-accent-blue-rgb), 0.08)', color: 'var(--hc-accent-blue)' }}
                            >
                                <MdCardMembership size={18} className="hc-text-primary" />
                            </span>
                            <h6 className="fw-bold mb-0 hc-text-primary">Subscription Period</h6>
                        </div>
                        <div className="px-4 pb-4">
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                        <p className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            <MdEventBusy size={14} className="hc-text-primary" /> Start Date
                                        </p>
                                        <p className="mb-0 fw-semibold text-dark">{startDate}</p>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                        <p className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            <MdEventBusy size={14} className="hc-text-primary" /> End Date
                                        </p>
                                        <p className="mb-0 fw-semibold text-dark">{endDate}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hc-surface hc-card-primary">
                        <div className="px-4 pt-4 pb-2 d-flex align-items-center gap-2">
                            <span
                                className="d-inline-flex align-items-center justify-content-center rounded-3"
                                style={{ width: 34, height: 34, backgroundColor: 'rgba(var(--hc-accent-blue-rgb), 0.08)', color: 'var(--hc-accent-blue)' }}
                            >
                                <MdLocationOn size={18} className="hc-text-primary" />
                            </span>
                            <h6 className="fw-bold mb-0 hc-text-primary">
                                Billing Information
                            </h6>
                        </div>
                        <div className="px-4 pb-4">
                            <div className="row g-3 mb-3">
                                <div className="col-12 col-md-6">
                                    <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                        <p className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            <MdEmail size={14} /> Email
                                        </p>
                                        <p className="mb-0 fw-semibold text-dark text-break" style={{ fontSize: '0.9rem' }}>
                                            {subscriber?.client?.billing_email || '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                        <p className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            <MdPhone size={14} /> Phone
                                        </p>
                                        <p className="mb-0 fw-semibold text-dark">{subscriber?.client?.billing_phone || '-'}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-4 border p-3 bg-light-subtle">
                                <p className="small text-muted mb-2 d-flex align-items-center gap-1">
                                    <MdLocationOn size={14} /> Address
                                </p>
                                <p className="mb-0 fw-semibold text-dark text-break" style={{ fontSize: '0.9rem' }}>
                                    {(() => {
                                        const addr = subscriber?.client?.billing_address;
                                        if (!addr) return '-';
                                        const parts = [
                                            addr.street,
                                            addr.city,
                                            addr.state?.trim(),
                                            addr.zipcode,
                                            addr.country,
                                        ].filter(Boolean);
                                        return parts.join(', ');
                                    })()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-lg-5">
                    <div className="hc-surface hc-card-primary h-100">
                        <div className="px-4 pt-4 pb-2 d-flex align-items-center gap-2">
                            <span
                                className="d-inline-flex align-items-center justify-content-center rounded-3"
                                style={{ width: 34, height: 34, backgroundColor: 'rgba(var(--hc-accent-blue-rgb), 0.08)', color: 'var(--hc-accent-blue)' }}
                            >
                                <MdHistory size={18} />
                            </span>
                            <h6 className="fw-bold mb-0 hc-text-primary">
                                Timeline & Ownership
                            </h6>
                        </div>
                        <div className="px-4 pb-4">
                            <div className="rounded-4 p-3 mb-3 bg-primary-subtle border border-primary-subtle">
                                <p className="small mb-1 text- d-flex align-items-center gap-1">
                                    <MdCalendarMonth size={14} /> Created On
                                </p>
                                <p className="mb-0 fw-semibold text-dark">{createdOn}</p>
                            </div>

                            <div className="rounded-4 p-3 mb-3 bg-primary-subtle border border-primary-subtle">
                                <p className="small mb-1 text-muted d-flex align-items-center gap-1">
                                    <MdCalendarMonth size={14} /> Modified On
                                </p>
                                <p className="mb-0 fw-semibold text-dark">{modifiedOn}</p>
                            </div>

                            <div className="rounded-4 p-3 bg-light border">
                                <p className="small text-muted mb-3">Ownership Trail</p>
                                <div className="row g-2">
                                    <div className="col-12 col-sm-6">
                                        <div className="bg-white border rounded-3 p-3 h-100">
                                            <div className="small text-muted mb-1">Created By</div>
                                            <div className="fw-semibold text-dark d-flex align-items-center gap-2">
                                                <MdPerson size={16} className="hc-text-primary" />
                                                <span>{subscriber?.created_by || '-'}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm-6">
                                        <div className="bg-white border rounded-3 p-3 h-100">
                                            <div className="small text-muted mb-1">Modified By</div>
                                            <div className="fw-semibold text-dark d-flex align-items-center gap-2">
                                                <MdPerson size={16} className="text-primary" />
                                                <span>{subscriber?.modified_by || '-'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <ExpandableTransactionPanel
                        token={token}
                        subId={id || null}
                        accountId={null}
                        title="Subscriber Transactions"
                        subtitle="Click a transaction card to reveal billing details for this subscriber."
                    />
                </div>
            </div>
        </div>
    );
}